/**
 * Copyright Highway9 Networks Inc.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
// NOTE: isSuppressed is intentionally derived via useMemo (not useState+useEffect) so it
// updates in the same render as goingOutOfSupportEdges, preventing a flash where edges are
// loaded but isSuppressed is still stale from the previous render.

interface Edge {
  id?: string;
  name?: string;
  goingOutOfSupport?: boolean;
  currentRelease?: string;
}

export const GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_KEY = "h9.goingOutOfSupportEdgeBanner.suppressed";
export const GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_EDGES_KEY = "h9.goingOutOfSupportEdgeBanner.suppressedEdges";
export const GOING_OUT_OF_SUPPORT_EDGE_CALLOUT_SUPPRESSED_KEY = "h9.goingOutOfSupportEdgeCallout.suppressed";
export const GOING_OUT_OF_SUPPORT_EDGE_CALLOUT_SUPPRESSED_EDGES_KEY = "h9.goingOutOfSupportEdgeCallout.suppressedEdges";

const GOING_OUT_OF_SUPPORT_RELEASE_ALERT_CHANGED_EVENT = "h9.goingOutOfSupportReleaseAlert.changed";
export const GOING_OUT_OF_SUPPORT_EDGE_FILTER_TRANSITION_EVENT = "h9.goingOutOfSupportEdgeFilter.transition";

function notifyGoingOutOfSupportReleaseAlertChanged() {
  window.dispatchEvent(new Event(GOING_OUT_OF_SUPPORT_RELEASE_ALERT_CHANGED_EVENT));
}

export function notifyGoingOutOfSupportEdgeFilterTransition() {
  window.dispatchEvent(new Event(GOING_OUT_OF_SUPPORT_EDGE_FILTER_TRANSITION_EVENT));
}

interface UseGoingOutOfSupportReleaseCalloutOptions {
  suppressedKey: string;
  suppressedEdgesKey: string;
}

interface UseGoingOutOfSupportReleaseCalloutResult {
  goingOutOfSupportEdges: Edge[];
  isSuppressed: boolean;
  suppress: () => void;
  clearSuppression: () => void;
}

function readSuppressedState(
  goingOutOfSupportEdges: Edge[],
  suppressedKey: string,
  suppressedEdgesKey: string
): boolean {
  if (goingOutOfSupportEdges.length === 0) return false;
  if (localStorage.getItem(suppressedKey) !== "true") return false;

  const raw = localStorage.getItem(suppressedEdgesKey);
  const suppressedNames: string[] = raw ? (JSON.parse(raw) as string[]) : [];
  const suppressedSet = new Set(suppressedNames);
  const hasNew = goingOutOfSupportEdges.some((e) => e.name && !suppressedSet.has(e.name));

  if (hasNew) {
    localStorage.removeItem(suppressedKey);
    localStorage.removeItem(suppressedEdgesKey);
    return false;
  }

  return true;
}

export function useGoingOutOfSupportReleaseCallout(
  edges: Edge[],
  { suppressedKey, suppressedEdgesKey }: UseGoingOutOfSupportReleaseCalloutOptions
): UseGoingOutOfSupportReleaseCalloutResult {
  const goingOutOfSupportEdges = useMemo(
    () => edges.filter((e) => e.goingOutOfSupport === true),
    [edges]
  );

  // alertRevision increments whenever suppress()/clearSuppression() write to localStorage,
  // so useMemo re-reads the updated value on the very next render.
  const [alertRevision, setAlertRevision] = useState(0);

  const isSuppressed = useMemo(
    () => readSuppressedState(goingOutOfSupportEdges, suppressedKey, suppressedEdgesKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goingOutOfSupportEdges, suppressedKey, suppressedEdgesKey, alertRevision]
  );

  useEffect(() => {
    const handleAlertChanged = () => setAlertRevision((revision) => revision + 1);
    window.addEventListener(GOING_OUT_OF_SUPPORT_RELEASE_ALERT_CHANGED_EVENT, handleAlertChanged);
    return () => window.removeEventListener(GOING_OUT_OF_SUPPORT_RELEASE_ALERT_CHANGED_EVENT, handleAlertChanged);
  }, []);

  const suppress = useCallback(() => {
    localStorage.setItem(suppressedKey, "true");
    localStorage.setItem(suppressedEdgesKey, JSON.stringify(goingOutOfSupportEdges.map((e) => e.name).filter(Boolean)));
    notifyGoingOutOfSupportReleaseAlertChanged();
  }, [suppressedKey, suppressedEdgesKey, goingOutOfSupportEdges]);

  const clearSuppression = useCallback(() => {
    localStorage.removeItem(suppressedKey);
    localStorage.removeItem(suppressedEdgesKey);
    notifyGoingOutOfSupportReleaseAlertChanged();
  }, [suppressedKey, suppressedEdgesKey]);

  return { goingOutOfSupportEdges, isSuppressed, suppress, clearSuppression };
}

export function useHasVisibleGoingOutOfSupportReleaseAlert(edges: Edge[]) {
  const banner = useGoingOutOfSupportReleaseCallout(edges, {
    suppressedKey: GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_KEY,
    suppressedEdgesKey: GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_EDGES_KEY,
  });
  const callout = useGoingOutOfSupportReleaseCallout(edges, {
    suppressedKey: GOING_OUT_OF_SUPPORT_EDGE_CALLOUT_SUPPRESSED_KEY,
    suppressedEdgesKey: GOING_OUT_OF_SUPPORT_EDGE_CALLOUT_SUPPRESSED_EDGES_KEY,
  });

  const hasGoingOutOfSupportEdges = banner.goingOutOfSupportEdges.length > 0;
  const bannerVisible = hasGoingOutOfSupportEdges && !banner.isSuppressed;
  const calloutVisible = hasGoingOutOfSupportEdges && !callout.isSuppressed;

  return bannerVisible || calloutVisible;
}

const GOING_OUT_OF_SUPPORT_FILTER_LOADER_MIN_MS = 200;

export function useGoingOutOfSupportEdgeFilterLoading(goingOutOfSupportOnly: string | null) {
  const showOnlyGoingOutOfSupportEdges = goingOutOfSupportOnly === "true";
  const [isLoading, setIsLoading] = useState(() => showOnlyGoingOutOfSupportEdges);

  useEffect(() => {
    const onTransition = () => setIsLoading(true);
    window.addEventListener(GOING_OUT_OF_SUPPORT_EDGE_FILTER_TRANSITION_EVENT, onTransition);
    return () => window.removeEventListener(GOING_OUT_OF_SUPPORT_EDGE_FILTER_TRANSITION_EVENT, onTransition);
  }, []);

  useEffect(() => {
    if (!isLoading) return;

    const timeoutId = window.setTimeout(() => setIsLoading(false), GOING_OUT_OF_SUPPORT_FILTER_LOADER_MIN_MS);
    return () => window.clearTimeout(timeoutId);
  }, [showOnlyGoingOutOfSupportEdges, isLoading]);

  const startGoingOutOfSupportFilterLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  return { isGoingOutOfSupportFilterLoading: isLoading, startGoingOutOfSupportFilterLoading };
}
